import type { FormikConfig, FormikProps, FormikValues } from 'formik';
import React from 'react';
import type { BaseModalProps } from '../ui/uiSlice';
import FormModal from './FormModal';
import type { ModalFooterPrimaryButton, ModalProps } from './Modal';
import Modal from './Modal';

const DEFAULT_WIDTH = 370;
const DEFAULT_CANCEL_BUTTON_TEXT = 'Cancel';

interface BaseConfirmationModalProps
  extends BaseModalProps,
    Omit<ModalProps, 'close' | 'variant' | 'children' | 'footer' | 'role'> {
  cancelButtonText?: string;
}

interface ConfirmationModalProps extends BaseConfirmationModalProps {
  confirmButton: ModalFooterPrimaryButton;
  children?: React.ReactNode;
}

const ConfirmationModal = ({
  close,
  confirmButton,
  cancelButtonText = DEFAULT_CANCEL_BUTTON_TEXT,
  children,
  ...modalProps
}: ConfirmationModalProps) => {
  return (
    <Modal
      {...modalProps}
      role="alertdialog"
      width={modalProps.width ?? DEFAULT_WIDTH}
      footer={{
        secondaryButton: { text: cancelButtonText, onClick: close },
        primaryButton:
          confirmButton.to == null
            ? {
                ...confirmButton,
                onClick: async () => {
                  await confirmButton.onClick();
                  close();
                },
              }
            : confirmButton,
      }}
    >
      {children}
    </Modal>
  );
};

type SubmitButtonProps = {
  text: string;
  danger?: boolean;
};

interface FormConfirmationModalProps<Values extends FormikValues>
  extends BaseConfirmationModalProps {
  children:
    | React.ReactNode
    | ((formik: FormikProps<Values>) => React.ReactNode);
  form: FormikConfig<Values>;
  submitButton: SubmitButtonProps;
}

export const FormConfirmationModal = <Values extends FormikValues>({
  children,
  cancelButtonText = DEFAULT_CANCEL_BUTTON_TEXT,
  submitButton,
  close,
  form,
  ...modalProps
}: FormConfirmationModalProps<Values>) => {
  return (
    <FormModal<Values>
      {...modalProps}
      role="alertdialog"
      width={modalProps.width ?? DEFAULT_WIDTH}
      form={{
        ...form,
        onSubmit: async (...args) => {
          await form?.onSubmit?.(...args);
          close();
        },
      }}
      footer={(props) => ({
        secondaryButton: {
          text: cancelButtonText,
          onClick: close,
        },
        submitButton: {
          text: submitButton.text,
          danger: submitButton.danger,
          disabled: !props.isValid || !props.dirty,
        },
      })}
    >
      {children}
    </FormModal>
  );
};

export default ConfirmationModal;
